/**
 * Utilidades para ordenamiento de datos en la aplicación
 */

/**
 * Ordena un array de objetos alfabéticamente por una propiedad específica
 * @param {Array} array - Array a ordenar
 * @param {string} property - Propiedad por la cual ordenar
 * @param {string} order - 'asc' (ascendente) o 'desc' (descendente)
 * @returns {Array} Array ordenado
 */
export const sortByProperty = (array, property, order = 'asc') => {
  if (!Array.isArray(array)) return [];
  
  return [...array].sort((a, b) => {
    const valueA = (a[property] || '').toString().toLowerCase();
    const valueB = (b[property] || '').toString().toLowerCase();
    
    if (order === 'asc') {
      return valueA.localeCompare(valueB, 'es', { sensitivity: 'base' });
    } else {
      return valueB.localeCompare(valueA, 'es', { sensitivity: 'base' });
    }
  });
};

/**
 * Ordena un array de strings alfabéticamente
 * @param {Array} array - Array de strings
 * @param {string} order - 'asc' o 'desc'
 * @returns {Array} Array ordenado
 */
export const sortStrings = (array, order = 'asc') => {
  if (!Array.isArray(array)) return [];
  
  return [...array].sort((a, b) => {
    const strA = (a || '').toString().toLowerCase();
    const strB = (b || '').toString().toLowerCase();
    
    if (order === 'asc') {
      return strA.localeCompare(strB, 'es', { sensitivity: 'base' });
    } else {
      return strB.localeCompare(strA, 'es', { sensitivity: 'base' });
    }
  });
};

/**
 * Ordena clientes alfabéticamente por nombre
 * @param {Array} clients - Array de clientes
 * @returns {Array} Clientes ordenados
 */
export const sortClients = (clients) => {
  return sortByProperty(clients, 'name', 'asc');
};

/**
 * Ordena sucursales alfabéticamente por nombre
 * @param {Array} branches - Array de sucursales
 * @returns {Array} Sucursales ordenadas
 */
export const sortBranches = (branches) => {
  return sortByProperty(branches, 'name', 'asc');
};

/**
 * Ordena técnicos alfabéticamente por nombre
 * @param {Array} technicians - Array de técnicos
 * @returns {Array} Técnicos ordenados
 */
export const sortTechnicians = (technicians) => {
  return sortByProperty(technicians, 'name', 'asc');
};

/**
 * Ordena productos/items por nombre
 * @param {Array} items - Array de productos
 * @returns {Array} Productos ordenados
 */
export const sortItems = (items) => {
  return sortByProperty(items, 'name', 'asc');
};

/**
 * Ordena opciones de select por label
 * @param {Array} options - Array de opciones {value, label}
 * @returns {Array} Opciones ordenadas
 */
export const sortSelectOptions = (options) => {
  return sortByProperty(options, 'label', 'asc');
};

/**
 * Ordena por fecha (más reciente primero por defecto)
 * @param {Array} array - Array de objetos con fecha
 * @param {string} dateProperty - Propiedad que contiene la fecha
 * @param {string} order - 'asc' (más antiguo primero) o 'desc' (más reciente primero)
 * @returns {Array} Array ordenado
 */
export const sortByDate = (array, dateProperty = 'createdAt', order = 'desc') => {
  if (!Array.isArray(array)) return [];
  
  return [...array].sort((a, b) => {
    const dateA = new Date(a[dateProperty]);
    const dateB = new Date(b[dateProperty]);
    
    if (order === 'desc') {
      return dateB - dateA; // Más reciente primero
    } else {
      return dateA - dateB; // Más antiguo primero
    }
  });
};

/**
 * Ordena por múltiples criterios
 * @param {Array} array - Array a ordenar
 * @param {Array} criteria - Array de objetos {property, order}
 * @returns {Array} Array ordenado
 */
export const sortByMultipleCriteria = (array, criteria) => {
  if (!Array.isArray(array) || !Array.isArray(criteria)) return array;
  
  return [...array].sort((a, b) => {
    for (const criterion of criteria) {
      const { property, order = 'asc' } = criterion;
      const valueA = (a[property] || '').toString().toLowerCase();
      const valueB = (b[property] || '').toString().toLowerCase();
      
      const comparison = valueA.localeCompare(valueB, 'es', { sensitivity: 'base' });
      
      if (comparison !== 0) {
        return order === 'asc' ? comparison : -comparison;
      }
    }
    return 0;
  });
};
